import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { response } from 'express';
import { PeriodService } from '../period.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-period',
  templateUrl: './period.component.html',
  styleUrl: './period.component.scss'
})
export class PeriodComponent implements OnInit {
  periods: any[] = []
  years: number[] = [];
  yearForm!: FormGroup; // FormGroup para el formulario
  selectedYear: number | null = new Date().getFullYear(); // Año actual por defecto
  periodForm!: FormGroup;
  availableYears: number[] = [];
  months = [
    { value: 1, label: 'Enero' }, { value: 2, label: 'Febrero' }, { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' }, { value: 5, label: 'Mayo' }, { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' }, { value: 8, label: 'Agosto' }, { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' }, { value: 11, label: 'Noviembre' }, { value: 12, label: 'Diciembre' }
  ];
  isEdit: boolean = false;
  editingPeriodId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private periodService: PeriodService
  ) {
    this.yearForm = this.fb.group({
      selectedYear: [this.selectedYear]  // Valor inicial null
    });
    this.periodForm = this.fb.group({
      year: [{ value: null, disabled: true }, Validators.required], // Año deshabilitado
      month: [{ value: null, disabled: true }, Validators.required], // Mes deshabilitado
      fmrValue: [null, [Validators.required, Validators.min(1)]], // FMR editable
      bencinaValue: [null, [Validators.required, Validators.min(1)]], // Bencina editable
      isClosed: [false, Validators.required] // Estado del período
    });
  }

  ngOnInit(): void {
    this.generateYears();
    this.loadNextPeriod();
    this.loadPeriods();
    // 🔹 Deshabilitar los controles de año y mes para evitar ediciones manuales
    this.periodForm.get('year')?.disable();
    this.periodForm.get('month')?.disable();
  }

  loadPeriods(): void {
    const selectedYear = this.yearForm.get('selectedYear')?.value; // Obtener el valor correctamente
    this.periodService.getView(selectedYear).subscribe({
      next: (response) => {
        this.periods = response;
      },
      error: (error) => {
        console.error("Error al cargar los períodos:", error);
      }
    });
  }



  getMonthLabel(): string {
    const monthNumber = this.periodForm.get('month')?.value || 1;
    const monthObj = this.months.find(m => m.value === monthNumber);
    return monthObj ? monthObj.label : '';
  }

  loadNextPeriod(): void {
    this.periodService.getLastPeriod().subscribe({
      next: (lastPeriod) => {
        let nextMonth = lastPeriod ? lastPeriod.month + 1 : new Date().getMonth() + 1;
        let nextYear = lastPeriod ? lastPeriod.year : new Date().getFullYear();

        if (nextMonth > 12) {
          nextMonth = 1;
          nextYear++;
        }

        // Definir el tipo explícitamente
        const variables: { name: string; value: number }[] = lastPeriod?.variables || [];

        this.periodForm.patchValue({
          year: nextYear,
          month: nextMonth,
          fmrValue: variables.find((v: { name: string; value: number }) => v.name === "FMR")?.value || 0,
          bencinaValue: variables.find((v: { name: string; value: number }) => v.name === "UF")?.value || 0
        });
      },
      error: (error) => {
        console.error("❌ Error al obtener el último período:", error);
      }
    });
  }

  savePeriod(): void {
    if (this.periodForm.invalid) {
      console.warn("Formulario inválido, revisa los errores.");
      return;
    }
    const formValues = this.periodForm.getRawValue(); // Obtener valores incluso deshabilitados
    const periodData = {
      name: `${formValues.month.toString().padStart(2, '0')}-${formValues.year}`,
      month: formValues.month,
      year: formValues.year,
      description: "Nuevo período generado automáticamente",
      isClosed: formValues.isClosed,
      variables: [
        { variableId: "67a0da951061ab171ff5eca8", value: formValues.fmrValue },
        { variableId: "67a0db0a1061ab171ff5ecaa", value: formValues.bencinaValue }
      ]
    };
    this.alertCharging()
    this.periodService.saveWithVariables(periodData).subscribe({
      next: (response) => {
        Swal.close()
        // 🔹 Mostrar alerta moderna
        Swal.fire({
          icon: 'success',
          title: 'Período guardado',
          text: 'El período ha sido guardado exitosamente.',
          timer: 2000, // Se cierra automáticamente en 2 segundos
          showConfirmButton: false
        });
        this.closeModal();
        this.loadNextPeriod();
        setTimeout(() => {
          this.loadPeriods();
        }, 600); // Espera 500ms antes de recargar
      },
      error: (error) => {
        Swal.close()
        console.error("Error al crear el período:", error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo guardar el período. Intenta nuevamente.',
        });
      }
    });
  }

  updatePeriod(): void {
    if (!this.editingPeriodId) return;

    const formValues = this.periodForm.getRawValue();

    const updatedData = {
      month: formValues.month,
      year: formValues.year,
      isClosed: formValues.isClosed,
      variables: [
        { variableId: "67a0da951061ab171ff5eca8", value: formValues.fmrValue },
        { variableId: "67a0db0a1061ab171ff5ecaa", value: formValues.bencinaValue }
      ]
    };
    this.alertCharging()
    this.periodService.updateWithVariables(this.editingPeriodId, updatedData).subscribe({
      next: (response) => {
        Swal.close()
        Swal.fire({
          icon: 'success',
          title: 'Período guardado',
          text: 'El período ha sido actualizado exitosamente.',
          timer: 3000, // Se cierra automáticamente en 2 segundos
          showConfirmButton: false
        });
        this.closeModal();
        this.loadNextPeriod();
        this.loadPeriods(); // Recargar la tabla
      },
      error: (error) => {
        console.error("❌ Error al actualizar el período:", error);
        Swal.close()
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo guardar el período. Intenta nuevamente.',
        });
      }
    });
  }

  alertCharging() {
    Swal.fire({
      title: 'Cargando...',
      allowOutsideClick: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });
  }

  get formControls() {
    return this.periodForm.controls;
  }

  generateYears(): void {
    const currentYear = new Date().getFullYear();
    const startYear = 2020; // Puedes ajustar el inicio del rango
    this.years = Array.from({ length: currentYear - startYear + 1 }, (_, index) => startYear + index);
  }

  onSubmit(): void {
    console.log('Año seleccionado:', this.yearForm.value.selectedYear);
  }

  openEditModal(periodo: any): void {
    this.isEdit = true;
    this.editingPeriodId = periodo.periodId;

    this.periodForm.patchValue({
      year: periodo.year,
      month: periodo.month,
      fmrValue: periodo.variables.find((v: any) => v.name === "FMR")?.value || 0,
      bencinaValue: periodo.variables.find((v: any) => v.name === "UF")?.value || 0,
      isClosed: periodo.isClosed
    });

    // 🔹 Abrir el modal manualmente sin Bootstrap como módulo
    const modalElement = document.getElementById('addPeriodModal');
    if (modalElement) {
      modalElement.classList.add('show'); // Agregar la clase "show"
      modalElement.style.display = 'block'; // Mostrar el modal
      modalElement.setAttribute('aria-modal', 'true'); // Accesibilidad
      modalElement.setAttribute('aria-hidden', 'false');

      // Agregar fondo oscuro manualmente
      let backdrop = document.createElement('div');
      backdrop.className = 'modal-backdrop fade show';
      document.body.appendChild(backdrop);

      document.body.classList.add('modal-open'); // Evita el scroll en el fondo
    }
  }
  closeModal(): void {
    // Reiniciar el formulario
    /* this.periodForm.reset({
      year: null,
      month: null,
      fmrValue: null,
      bencinaValue: null,
      isClosed: false
    }); */

    this.isEdit = false;
    this.editingPeriodId = null;
    const modalElement = document.getElementById('addPeriodModal');
    if (modalElement) {
      modalElement.classList.remove('show'); // Oculta el modal visualmente
      modalElement.setAttribute('aria-hidden', 'true'); // Indica que está oculto
      modalElement.style.display = 'none'; // Oculta el modal
      document.body.classList.remove('modal-open'); // Elimina la clase del body
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop) {
        backdrop.remove(); // Remueve el fondo oscuro
      }
    }
  }

}
